import { createId } from "@paralleldrive/cuid2";
import type { ResumeData } from "@reactive-resume/schema";
import { defaultResumeData } from "@reactive-resume/schema";
import * as mammoth from "mammoth";

export class DocxParser {
  async readFile(file: File): Promise<string> {
    const arrayBuffer = await file.arrayBuffer();

    // Mammoth extractRawText
    const result = await mammoth.extractRawText({ arrayBuffer });
    return result.value; // The raw text
  }

  validate(data: string): ResumeData {
    if (!data || data.trim().length === 0) {
      throw new Error("No text content found in Docx");
    }
    return JSON.parse(JSON.stringify(defaultResumeData));
  }

  convert(text: string): ResumeData {
    const data = JSON.parse(JSON.stringify(defaultResumeData)) as ResumeData;
    const lines = text.split("\n");
    let currentSection = "summary";

    const emailRegex = /\b[\w%+.-]+@[\d.a-z-]+\.[a-z]{2,}\b/i;
    const phoneRegex = /(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}/;
    const urlRegex =
      /https?:\/\/(www\.)?[\w#%+.:=@~-]{1,256}\.[\d()A-Za-z]{1,6}\b([\w#%&()+./:=?@~-]*)/;

    // Profile Data Extraction (Top 30 lines)
    const firstChunkLines = lines.slice(0, 30);
    const firstChunk = firstChunkLines.join(" ");

    const emailMatch = emailRegex.exec(firstChunk);
    if (emailMatch) data.basics.email = emailMatch[0];

    const phoneMatch = phoneRegex.exec(firstChunk);
    if (phoneMatch) data.basics.phone = phoneMatch[0];

    const urlMatch = urlRegex.exec(firstChunk);
    if (urlMatch) {
      data.basics.url = {
        label: "Website",
        href: urlMatch[0],
      };
    }

    // Name Heuristic
    for (const line of firstChunkLines) {
      if (!line.trim()) continue;
      if (emailRegex.test(line)) continue;
      if (phoneRegex.test(line)) continue;
      if (urlRegex.test(line)) continue;
      data.basics.name = line.trim();
      break;
    }

    // Location Heuristic
    for (const line of firstChunkLines) {
      if (data.basics.location) break;
      if (line.includes(data.basics.name)) continue;
      if (/^[\sA-Za-z]+,\s*[\sA-Za-z]+$/.test(line.trim())) {
        data.basics.location = line.trim();
      }
    }

    const sections = {
      experience: ["experience", "employment", "work history", "work experience"],
      education: ["education", "academic", "qualifications"],
      skills: ["skills", "technologies", "competencies"],
      summary: ["summary", "profile", "about me", "objective"],
      projects: ["projects", "personal projects"],
      certifications: ["certifications", "awards", "honors", "achievements"],
    };

    // Helper to check if a line is a header
    const isHeaderLine = (line: string, keywords: string[]) => {
      const clean = line.trim().toLowerCase();
      return (
        clean.length < 40 &&
        keywords.some(
          (k) =>
            clean === k ||
            clean === k + ":" ||
            clean.startsWith(k + " ") ||
            (clean.startsWith(k) && clean.includes("___"))
        )
      );
    };

    // Helper to detect a date range
    const hasDateRange = (line: string) => {
      return /\d{4}.*\d{4}|present|current/i.test(line);
    };

    // Helper to check if line is a bullet point
    const isBulletLine = (line: string) => {
      return /^\s*[*•\-]/.test(line);
    };

    for (const line of lines) {
      const cleanLine = line.trim().toLowerCase();
      let foundSection = false;

      // Detect Section Change
      if (cleanLine.length < 50) {
        if (isHeaderLine(line, sections.experience)) {
          currentSection = "experience";
          foundSection = true;
          data.sections.experience.items.push({
            id: createId(),
            company: "Parsed Experience",
            position: "",
            location: "",
            date: "",
            summary: "",
            url: { label: "", href: "" },
            visible: true,
          });
        } else if (isHeaderLine(line, sections.education)) {
          currentSection = "education";
          foundSection = true;
          data.sections.education.items.push({
            id: createId(),
            institution: "Parsed Education",
            studyType: "",
            area: "",
            score: "",
            date: "",
            summary: "",
            url: { label: "", href: "" },
            visible: true,
          });
        } else if (isHeaderLine(line, sections.skills)) {
          currentSection = "skills";
          foundSection = true;
          if (data.sections.skills.items.length === 0) {
            data.sections.skills.items.push({
              id: createId(),
              name: "Parsed Skills",
              description: "",
              level: 1,
              keywords: [],
              visible: true,
            });
          }
        } else if (isHeaderLine(line, sections.summary)) {
          currentSection = "summary";
          foundSection = true;
        } else if (isHeaderLine(line, sections.projects)) {
          currentSection = "projects";
          foundSection = true;
          data.sections.projects.items.push({
            id: createId(),
            name: "Parsed Project",
            description: "",
            date: "",
            summary: "",
            keywords: [],
            url: { label: "", href: "" },
            visible: true,
          });
        } else if (isHeaderLine(line, sections.certifications)) {
          currentSection = "certifications";
          foundSection = true;
          data.sections.certifications.items.push({
            id: createId(),
            name: "Parsed Certification",
            issuer: "",
            date: "",
            summary: "",
            url: { label: "", href: "" },
            visible: true,
          });
        }
      }

      if (foundSection) continue;

      // Process Content
      // Process Content
      switch (currentSection) {
        case "summary":
          data.sections.summary.content += line + "\n";
          break;

        case "experience":
          if (hasDateRange(line)) {
            data.sections.experience.items.push({
              id: createId(),
              company: "Parsed Experience",
              position: "",
              location: "",
              date: line,
              summary: "",
              url: { label: "", href: "" },
              visible: true,
            });
            continue;
          }

          if (data.sections.experience.items.length === 0) {
            data.sections.experience.items.push({
              id: createId(),
              company: "Parsed Experience",
              position: "",
              location: "",
              date: "",
              summary: "",
              url: { label: "", href: "" },
              visible: true,
            });
          }
          data.sections.experience.items.at(-1)!.summary += line + "\n";
          break;

        case "education":
          if (hasDateRange(line) && data.sections.education.items.length > 0) {
            const lastItem = data.sections.education.items.at(-1)!;
            if (lastItem.summary.length > 20) {
              data.sections.education.items.push({
                id: createId(),
                institution: "Parsed Education",
                studyType: "",
                area: "",
                score: "",
                date: line,
                summary: "",
                url: { label: "", href: "" },
                visible: true,
              });
              continue;
            }
          }
          if (data.sections.education.items.length === 0) {
            data.sections.education.items.push({
              id: createId(),
              institution: "Parsed Education",
              studyType: "",
              area: "",
              score: "",
              date: "",
              summary: "",
              url: { label: "", href: "" },
              visible: true,
            });
          }
          data.sections.education.items.at(-1)!.summary += line + "\n";
          break;

        case "projects":
          const looksLikeTitle =
            line.trim().length > 0 && line.trim().length < 60 && !isBulletLine(line);
          const lastProject = data.sections.projects.items.at(-1);

          if (
            data.sections.projects.items.length === 0 ||
            (looksLikeTitle && lastProject && lastProject.summary.length > 10)
          ) {
            data.sections.projects.items.push({
              id: createId(),
              name: line.trim() || "Parsed Project", // Use the line as name
              description: "",
              date: "",
              summary: line.trim().length === 0 ? "" : line + "\n",
              keywords: [],
              url: { label: "", href: "" },
              visible: true,
            });
          } else {
            data.sections.projects.items.at(-1)!.summary += line + "\n";
          }
          break;

        case "certifications":
          if (data.sections.certifications.items.length === 0) {
            data.sections.certifications.items.push({
              id: createId(),
              name: "Parsed Certification",
              issuer: "",
              date: "",
              summary: "",
              url: { label: "", href: "" },
              visible: true,
            });
          }
          data.sections.certifications.items.at(-1)!.summary += line + "\n";
          break;

        case "skills":
          {
            const skills = line.includes(",") ? line.split(",") : [line];
            const cleanSkills = skills.map((s) => s.trim()).filter((s) => s.length > 0);

            if (data.sections.skills.items.length === 0) {
              data.sections.skills.items.push({
                id: createId(),
                name: "Parsed Skills",
                description: "",
                level: 1,
                keywords: [],
                visible: true,
              });
            }
            if (cleanSkills.length > 0) {
              data.sections.skills.items[0].keywords.push(...cleanSkills);
            }
          }
          break;
      }
    }

    return data;
  }
}

